import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { ColorName } from '@poseidon/components/Input2';
import { Button, FilterSelect } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import type { BarActionSpec, BarDisplay } from './selectionBarLayout';
import {
  BAR_SIDE_MARGIN,
  computeSelectionBarLayout,
  estimateLabeledWidth,
  ICON_BUTTON_WIDTH,
} from './selectionBarLayout';
import { useSelectionBarAvailableWidth } from './SelectPopupContainer';

/** A plain button: rendered by the bar, can shrink to its icon and then move into "More". */
type ButtonAction = {
  kind: 'button';
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: ReactNode;
  color?: ColorName;
  /** Already icon-only at full width (e.g. Resume / Pause). The label is still used in More. */
  iconOnly?: boolean;
};

/** A select or grouped control: renders itself and can only shrink to its icon. */
type ControlAction = {
  kind: 'control';
  render: (showText: boolean) => ReactNode;
  labeledWidth: number;
  iconWidth: number;
};

export type SelectionBarAction = (ButtonAction | ControlAction) & {
  id: string;
  /** Primary actions stay visible (and labelled) longest. */
  primary?: boolean;
  /** Lower = more important. */
  priority: number;
};

function toSpec(a: SelectionBarAction): BarActionSpec {
  if (a.kind === 'control') {
    return {
      id: a.id,
      primary: a.primary ?? false,
      priority: a.priority,
      overflowable: false,
      labeledWidth: a.labeledWidth,
      iconWidth: a.iconWidth,
    };
  }
  const labeledWidth = a.iconOnly ? ICON_BUTTON_WIDTH : estimateLabeledWidth(a.label);
  return {
    id: a.id,
    primary: a.primary ?? false,
    priority: a.priority,
    overflowable: true,
    labeledWidth,
    iconWidth: ICON_BUTTON_WIDTH,
  };
}

function renderAction(a: SelectionBarAction, display: BarDisplay): ReactNode {
  if (a.kind === 'control') return a.render(display === 'label');
  const showLabel = display === 'label' && !a.iconOnly;
  return (
    <Button
      size="sm"
      icon={a.icon}
      color={a.color}
      onClick={a.onClick}
      disabled={a.disabled}
      aria-label={a.label}
      tooltip={a.tooltip ?? (showLabel ? undefined : a.label)}
      data-bar-action={a.id}
      data-bar-display={showLabel ? 'label' : 'icon'}>
      {showLabel ? a.label : undefined}
    </Button>
  );
}

/**
 * The bulk-selection bar's actions, laid out for the width the bar has: labels where they fit,
 * icons where they don't, and the least important buttons in a "More" menu when even icons don't.
 * Order on screen follows `actions`; collapse order follows `priority` (see computeSelectionBarLayout).
 * Render inside `SelectPopupContainer`, which supplies the available width.
 */
export function SelectionBarActions({ actions }: { actions: SelectionBarAction[] }) {
  const containerWidth = useSelectionBarAvailableWidth();
  const available = containerWidth == null ? Number.POSITIVE_INFINITY : containerWidth - BAR_SIDE_MARGIN;

  const layout = useMemo(() => computeSelectionBarLayout(actions.map(toSpec), available), [actions, available]);
  const overflowed = actions.filter(
    (a): a is SelectionBarAction & ButtonAction => a.kind === 'button' && layout.display[a.id] === 'more',
  );

  return (
    <>
      {actions
        .filter((a) => layout.display[a.id] !== 'more')
        .map((a) => (
          <span key={a.id} className="contents" data-bar-slot={a.id} data-bar-display={layout.display[a.id]}>
            {renderAction(a, layout.display[a.id] ?? 'label')}
          </span>
        ))}
      {overflowed.length > 0 && (
        <FilterSelect
          size="sm"
          icon={<General.DotsHorizontal />}
          value=""
          options={overflowed.map((a) => ({
            value: a.id,
            label: a.label,
            icon: a.icon,
            // The tooltip carries the detail a label can't (credits, skip rules) — keep it in the menu.
            description: a.disabled ? 'Working…' : typeof a.tooltip === 'string' ? a.tooltip : undefined,
          }))}
          onChange={(id) => {
            const action = overflowed.find((a) => a.id === id);
            if (action != null && !action.disabled) action.onClick();
          }}
          tooltip="More actions"
          aria-label="More actions"
          filterable={false}
          showText={false}
          autoWidth
          popoverPlacement="top-end"
          data-bar-more
        />
      )}
    </>
  );
}
